// Performance Monitor - Production Performance Optimization
import analytics from './analytics.js';

export class PerformanceMonitor {
    constructor() {
        this.metrics = {};
        this.resourceTimings = [];
        this.initialized = false;
        this.observer = null;
    }

    init() {
        if (this.initialized) return;
        
        // Start monitoring immediately
        this.startResourceTiming();
        this.monitorLongTasks();
        this.setupPerformanceObserver();
        this.monitorMemoryUsage();
        this.trackCoreWebVitals();
        
        // Lazy load images
        this.setupLazyLoading();
        
        // Optimize animations
        this.optimizeAnimations();
        
        // Setup service worker for caching
        this.registerServiceWorker();
        
        this.initialized = true;
        
        // Report initial metrics after page load
        if (document.readyState === 'complete') {
            this.reportMetrics();
        } else {
            window.addEventListener('load', () => this.reportMetrics());
        }
    }

    startResourceTiming() {
        if (!('performance' in window)) return;
        
        // Monitor resource loading
        const resources = performance.getEntriesByType('resource');
        this.resourceTimings = resources.map(resource => ({
            name: resource.name,
            type: this.getResourceType(resource.name),
            duration: Math.round(resource.duration),
            size: resource.transferSize || 0,
            cached: resource.transferSize === 0,
            protocol: resource.nextHopProtocol
        }));
        
        // Find slow resources
        const slowResources = this.resourceTimings.filter(r => r.duration > 1000);
        if (slowResources.length > 0) {
            console.warn('Slow resources detected:', slowResources);
            this.optimizeSlowResources(slowResources);
        }
    }

    getResourceType(url) {
        const extension = url.split('.').pop().split('?')[0].toLowerCase();
        const typeMap = {
            'js': 'script',
            'css': 'stylesheet',
            'jpg': 'image',
            'jpeg': 'image',
            'png': 'image',
            'webp': 'image',
            'svg': 'image',
            'woff': 'font',
            'woff2': 'font',
            'ttf': 'font',
            'json': 'data'
        };
        return typeMap[extension] || 'other';
    }

    optimizeSlowResources(resources) {
        resources.forEach(resource => {
            if (resource.type === 'image' && resource.size > 100000) {
                console.warn(`Large image detected: ${resource.name} (${Math.round(resource.size / 1024)}KB)`);
                // Could implement dynamic image optimization here
            }
        });
    }

    monitorLongTasks() {
        if (!('PerformanceObserver' in window)) return;
        
        try {
            const observer = new PerformanceObserver((list) => {
                for (const entry of list.getEntries()) {
                    // Long task detected (> 50ms)
                    console.warn('Long task detected:', {
                        duration: Math.round(entry.duration),
                        startTime: Math.round(entry.startTime)
                    });
                    
                    // Track long tasks
                    analytics.trackEvent('Performance', 'LongTask', 'ms', Math.round(entry.duration));
                }
            });
            
            observer.observe({ entryTypes: ['longtask'] });
        } catch (e) {
            // Long task monitoring not supported
        }
    }

    setupPerformanceObserver() {
        if (!('PerformanceObserver' in window)) return;
        
        // First Contentful Paint
        try {
            const paintObserver = new PerformanceObserver((list) => {
                for (const entry of list.getEntries()) {
                    if (entry.name === 'first-contentful-paint') {
                        this.metrics.fcp = Math.round(entry.startTime);
                        analytics.trackEvent('Performance', 'FCP', 'ms', this.metrics.fcp);
                    }
                }
            });
            paintObserver.observe({ entryTypes: ['paint'] });
        } catch (e) {}
        
        // Layout Shift
        try {
            let clsValue = 0;
            let clsEntries = [];
            
            const layoutShiftObserver = new PerformanceObserver((list) => {
                for (const entry of list.getEntries()) {
                    if (!entry.hadRecentInput) {
                        clsValue += entry.value;
                        clsEntries.push(entry);
                    }
                }
            });
            
            layoutShiftObserver.observe({ entryTypes: ['layout-shift'] });
            
            // Report CLS when page is hidden
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    this.metrics.cls = Math.round(clsValue * 1000) / 1000;
                    analytics.trackEvent('Performance', 'CLS', 'score', this.metrics.cls);
                }
            });
        } catch (e) {}
    }

    monitorMemoryUsage() {
        if (!performance.memory) return;
        
        // Check memory usage periodically
        setInterval(() => {
            const memInfo = performance.memory;
            const usedMB = Math.round(memInfo.usedJSHeapSize / 1048576);
            const totalMB = Math.round(memInfo.totalJSHeapSize / 1048576);
            const limitMB = Math.round(memInfo.jsHeapSizeLimit / 1048576);
            
            // Warn if memory usage is high
            const usage = usedMB / limitMB;
            if (usage > 0.9) {
                console.warn('High memory usage detected:', `${usedMB}MB / ${limitMB}MB`);
                this.triggerMemoryCleanup();
            }
            
            // Store metrics
            this.metrics.memoryUsage = {
                used: usedMB,
                total: totalMB,
                limit: limitMB,
                percentage: Math.round(usage * 100)
            };
        }, 30000); // Every 30 seconds
    }

    triggerMemoryCleanup() {
        // Clear caches
        if ('caches' in window) {
            caches.keys().then(names => {
                names.forEach(name => {
                    if (name.includes('temp-')) {
                        caches.delete(name);
                    }
                });
            });
        }
        
        // Clear expired session storage
        this.cleanupStorage();
        
        // Suggest garbage collection (if available)
        if (window.gc) {
            window.gc();
        }
    }

    cleanupStorage() {
        // Clean up old session storage items
        const now = Date.now();
        const maxAge = 3600000; // 1 hour
        
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith('temp_')) {
                try {
                    const data = JSON.parse(sessionStorage.getItem(key));
                    if (data.timestamp && (now - data.timestamp) > maxAge) {
                        sessionStorage.removeItem(key);
                    }
                } catch (e) {
                    // Invalid data, remove it
                    sessionStorage.removeItem(key);
                }
            }
        }
    }

    trackCoreWebVitals() {
        if (!('web-vitals' in window)) {
            // Implement basic Core Web Vitals tracking
            this.trackBasicVitals();
        }
    }

    trackBasicVitals() {
        // LCP - Largest Contentful Paint
        const lcpObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            const lastEntry = entries[entries.length - 1];
            this.metrics.lcp = Math.round(lastEntry.renderTime || lastEntry.loadTime);
        });
        
        try {
            lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        } catch (e) {}
        
        // FID - First Input Delay
        let fidValue = null;
        const fidObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            entries.forEach(entry => {
                if (!fidValue) {
                    fidValue = Math.round(entry.processingStart - entry.startTime);
                    this.metrics.fid = fidValue;
                    analytics.trackEvent('Performance', 'FID', 'ms', fidValue);
                }
            });
        });
        
        try {
            fidObserver.observe({ entryTypes: ['first-input'] });
        } catch (e) {}
    }

    setupLazyLoading() {
        // Native lazy loading for images
        const images = document.querySelectorAll('img[loading="lazy"]');
        
        // Fallback for browsers without native lazy loading
        if (!('loading' in HTMLImageElement.prototype)) {
            this.setupIntersectionObserverLazyLoad();
        }
        
        // Preload critical images
        this.preloadCriticalImages();
    }

    setupIntersectionObserverLazyLoad() {
        const imageObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const img = entry.target;
                    const src = img.getAttribute('data-src');
                    if (src) {
                        img.src = src;
                        img.removeAttribute('data-src');
                        imageObserver.unobserve(img);
                    }
                }
            });
        }, {
            rootMargin: '50px'
        });
        
        document.querySelectorAll('img[data-src]').forEach(img => {
            imageObserver.observe(img);
        });
    }

    preloadCriticalImages() {
        // Preload hero images and above-the-fold content
        const criticalImages = [
            '/assets/images/hero-bg.svg',
            '/assets/images/logo.svg'
        ];
        
        criticalImages.forEach(src => {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = src;
            document.head.appendChild(link);
        });
    }

    optimizeAnimations() {
        // Reduce motion for users who prefer it
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        
        if (prefersReducedMotion.matches) {
            document.documentElement.classList.add('reduce-motion');
            this.disableComplexAnimations();
        }
        
        // Pause animations when tab is hidden
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.pauseAnimations();
            } else {
                this.resumeAnimations();
            }
        });
        
        // Use CSS containment for better performance
        this.applyCSSContainment();
    }

    disableComplexAnimations() {
        // Disable particle canvas
        const canvas = document.getElementById('particleCanvas');
        if (canvas) {
            canvas.style.display = 'none';
        }
        
        // Reduce animation durations
        const style = document.createElement('style');
        style.textContent = `
            .reduce-motion * {
                animation-duration: 0.01ms !important;
                transition-duration: 0.01ms !important;
            }
        `;
        document.head.appendChild(style);
    }

    pauseAnimations() {
        document.querySelectorAll('*').forEach(el => {
            const computedStyle = window.getComputedStyle(el);
            if (computedStyle.animationName !== 'none') {
                el.style.animationPlayState = 'paused';
            }
        });
    }

    resumeAnimations() {
        document.querySelectorAll('*').forEach(el => {
            if (el.style.animationPlayState === 'paused') {
                el.style.animationPlayState = 'running';
            }
        });
    }

    applyCSSContainment() {
        // Apply CSS containment to improve rendering performance
        const style = document.createElement('style');
        style.textContent = `
            .feature-card, .stat-card, .insight-card {
                contain: layout style paint;
            }
            
            .section {
                contain: layout;
            }
            
            img {
                contain: paint;
            }
        `;
        document.head.appendChild(style);
    }

    async registerServiceWorker() {
        if (!('serviceWorker' in navigator)) return;
        
        try {
            const registration = await navigator.serviceWorker.register('/sw.js');
            console.log('Service Worker registered:', registration.scope);
            
            // Check for updates periodically
            setInterval(() => {
                registration.update();
            }, 3600000); // Every hour
            
        } catch (error) {
            console.error('Service Worker registration failed:', error);
        }
    }

    reportMetrics() {
        // Gather all performance metrics
        const navTiming = performance.getEntriesByType('navigation')[0];
        
        if (navTiming) {
            this.metrics = {
                ...this.metrics,
                dns: Math.round(navTiming.domainLookupEnd - navTiming.domainLookupStart),
                tcp: Math.round(navTiming.connectEnd - navTiming.connectStart),
                ttfb: Math.round(navTiming.responseStart - navTiming.requestStart),
                download: Math.round(navTiming.responseEnd - navTiming.responseStart),
                domInteractive: Math.round(navTiming.domInteractive),
                domComplete: Math.round(navTiming.domComplete),
                loadComplete: Math.round(navTiming.loadEventEnd - navTiming.loadEventStart),
                totalTime: Math.round(navTiming.loadEventEnd - navTiming.fetchStart)
            };
        }
        
        // Calculate performance score
        const score = this.calculatePerformanceScore();
        this.metrics.score = score;
        
        // Send metrics to analytics
        analytics.track('performance_report', {
            ...this.metrics,
            url: window.location.href,
            userAgent: navigator.userAgent,
            connection: this.getConnectionInfo()
        });
        
        // Log performance summary
        this.logPerformanceSummary();
    }

    calculatePerformanceScore() {
        // Simple performance scoring based on key metrics
        let score = 100;
        
        // Deduct points for slow metrics
        if (this.metrics.fcp > 1800) score -= 10;
        if (this.metrics.lcp > 2500) score -= 15;
        if (this.metrics.fid > 100) score -= 10;
        if (this.metrics.cls > 0.1) score -= 10;
        if (this.metrics.ttfb > 600) score -= 5;
        
        // Bonus for fast load
        if (this.metrics.totalTime < 3000) score += 5;
        
        return Math.max(0, Math.min(100, score));
    }

    getConnectionInfo() {
        if (!navigator.connection) return null;
        
        return {
            effectiveType: navigator.connection.effectiveType,
            downlink: navigator.connection.downlink,
            rtt: navigator.connection.rtt,
            saveData: navigator.connection.saveData
        };
    }

    logPerformanceSummary() {
        if (this.metrics.score >= 90) {
            console.log('%c⚡ Excellent Performance!', 'color: green; font-size: 16px; font-weight: bold');
        } else if (this.metrics.score >= 70) {
            console.log('%c⚡ Good Performance', 'color: orange; font-size: 16px; font-weight: bold');
        } else {
            console.log('%c⚠️ Performance Needs Improvement', 'color: red; font-size: 16px; font-weight: bold');
        }
        
        console.table({
            'First Contentful Paint': `${this.metrics.fcp || 'N/A'}ms`,
            'Largest Contentful Paint': `${this.metrics.lcp || 'N/A'}ms`,
            'First Input Delay': `${this.metrics.fid || 'N/A'}ms`,
            'Cumulative Layout Shift': this.metrics.cls || 'N/A',
            'Total Load Time': `${this.metrics.totalTime || 'N/A'}ms`,
            'Performance Score': `${this.metrics.score}/100`
        });
    }

    // Public API
    getMetrics() {
        return { ...this.metrics };
    }

    getResourceTimings() {
        return [...this.resourceTimings];
    }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();
export default performanceMonitor;